import { DEFAULT_MAX_QUERY_RETRIES } from '../../config/query-retry';
import type { MembershipsResponse } from '../../services/auth/auth.service';
import { getHttpErrorStatus } from '../../utils/error.utils';

/**
 * Extra retry budget for HTTP 503 during auth bootstrap. A 503 from
 * GET /auth/memberships means the JIT-provisioned user isn't visible to the DB
 * yet (see user-provisioning.service.ts) — almost always transient. Retrying
 * with TanStack Query's default exponential backoff (1s/2s/4s/8s before
 * attempts 2-5) for up to 4 retries sums to ~15s, matching
 * PROVISIONING_GRACE_MS (use-provisioning-grace.ts), so a permanent 503 (e.g.
 * an email collision on a different clerkId) still surfaces BootstrapError
 * instead of retrying forever.
 */
const MEMBERSHIPS_503_MAX_RETRIES = 4;

export function shouldRetryMembershipsQuery(failureCount: number, error: unknown): boolean {
  const status = getHttpErrorStatus(error);
  if (status === 503) {
    return failureCount < MEMBERSHIPS_503_MAX_RETRIES;
  }
  return failureCount < DEFAULT_MAX_QUERY_RETRIES;
}

const EMPTY_LIST_POLL_MS = 3000;

/**
 * An empty list normally means "JIT provisioning still in flight", so it is
 * polled. When the user is a consultant blocked on every company the list is
 * empty by design and no amount of polling changes that.
 */
export function getMembershipsRefetchInterval(
  data: MembershipsResponse | undefined,
): number | false {
  if (!data || data.consultantAccessBlocked) {
    return false;
  }
  return data.memberships.length === 0 ? EMPTY_LIST_POLL_MS : false;
}
